// Per-market customizations for the landing page.
//
// As new design treatments roll out state-by-state, add an entry here keyed by
// the market code (uppercase). Anything not listed falls back to
// DEFAULT_MARKET_CUSTOMIZATION (the legacy / classic experience).
//
// Currently controls:
//   - recallBannerVariant: which recall banner UI to render when isOnRecall is true
//       'urgent'  = new "URGENT SAFETY RECALL" banner inside the state header
//       'classic' = original yellow alert box below the state header
//       'colorado' = non-dismissible alert above all content, with MCA notice links
//   - recallMoreInfoHref: optional URL used by the urgent banner's
//       "click here for more information." link. Required for variant 'urgent'.
//   - verifiedBadgeSrc: optional URL of a "tested / regulated / legal" style
//       badge displayed in the state header banner.
//   - stateLogoSrcOverride: optional URL that replaces the BE-provided
//       regulator logo (e.g. a combined seal + wordmark image).
//   - licenseeNotice: notice below the tabs, visible for both product and brand content.
//
// Colorado uses MCA Administrative Recall (isOnRecall), never holds. Its landing
// API refreshes recall status on each load and supplies PublicHealthNoticeUrls.
// Open Colorado pages poll that API every 60 seconds for the interim beta path;
// the banner has no regulator-website fallback and suppresses the verified badge.
// Requests time out after 30 seconds; refresh errors retain the last known recall
// information, display an error, and retry on the next polling interval.
// Polling and rendering resolve the market from the same regulatory metadata fallback.

export type RecallBannerVariant = 'urgent' | 'classic' | 'colorado';

export type LicenseeNotice = {
  heading: string;
  message: string;
};

export type MarketCustomization = {
  recallBannerVariant: RecallBannerVariant;
  recallMoreInfoHref?: string;
  verifiedBadgeSrc?: string;
  stateLogoSrcOverride?: string;
  licenseeNotice?: LicenseeNotice;
};

export const DEFAULT_MARKET_CUSTOMIZATION: MarketCustomization = {
  recallBannerVariant: 'classic',
};

// Markets with custom landing-page treatment. Keys must be uppercase market codes.
const MARKET_CUSTOMIZATIONS: Record<string, MarketCustomization> = {
  CO: {
    recallBannerVariant: 'colorado',
    verifiedBadgeSrc: '/images/outlined-metrc-badge.png',
    licenseeNotice: {
      heading: 'Supplied by the licensee.',
      message: 'The product details and brand information below are provided by the licensee. They are not provided by the State of Colorado and are not verified against state records.',
    },
  },
  NY: {
    recallBannerVariant: 'urgent',
    recallMoreInfoHref: 'https://cannabis.ny.gov/recalls',
    verifiedBadgeSrc: '/images/outlined-metrc-badge.png',
  },
};

export function getMarketCustomization(marketCode: string | undefined | null): MarketCustomization {
  const code = String(marketCode ?? '').trim().toUpperCase();
  if (!code) return DEFAULT_MARKET_CUSTOMIZATION;
  return MARKET_CUSTOMIZATIONS[code] ?? DEFAULT_MARKET_CUSTOMIZATION;
}

// Map of full US state names to 2-letter codes for markets we currently care about.
// Extend as new market customizations are added above.
const STATE_NAME_TO_CODE: Record<string, string> = {
  'NEW YORK': 'NY',
  CALIFORNIA: 'CA',
  COLORADO: 'CO',
};

// Best-effort fallback when the BE doesn't expose a market code on the regulatory
// card. Scans free-text fields (e.g. the regulator description) for a known state
// name and returns its 2-letter code, or undefined if nothing matches.
export function inferMarketCodeFromText(...sources: Array<string | undefined | null>): string | undefined {
  const haystack = sources.map((s) => String(s ?? '')).join(' ').toUpperCase();
  if (!haystack) return undefined;
  for (const [name, code] of Object.entries(STATE_NAME_TO_CODE)) {
    if (haystack.includes(name)) return code;
  }
  return undefined;
}

export function resolveLandingMarketCode(
  marketCode: unknown,
  regulatoryCard?: {
    marketCode?: string;
    market?: string;
    marketName?: string;
    description?: string;
  } | null,
): string | undefined {
  const normalize = (value: unknown) => {
    const code = String(value ?? '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : undefined;
  };
  return normalize(marketCode)
    || normalize(regulatoryCard?.marketCode)
    || normalize(regulatoryCard?.market)
    || inferMarketCodeFromText(
      regulatoryCard?.market,
      regulatoryCard?.marketName,
      regulatoryCard?.description,
    );
}
